import React, { useEffect, useRef, useState, useCallback } from "react";
import trafficIcon from '../../images/traffic.svg';
import userIcon from '../../images/user.svg';
import openIcon from '../../images/open.svg';
import '../../universal.css';
import airlines from "../../data/airlines.json";
import atypData from "../../data/ICAO/atyp.json";
import airportData from "../../data/ICAO/airports.json";
import airplaneIcon from '../../images/tfcBlue.svg';
import onGroundIcon from '../../images/on_ground.svg';

import { URL_START } from '../../data/config';

import medium_jet from '../../images/traffic/medium_jet.png';
import two_engine_heavy_jet from '../../images/traffic/two_engine_heavy_jet.png';
import light_jet from '../../images/traffic/light_jet.png';
import four_engine_medium_jet from '../../images/traffic/four_engine_medium_jet.png';
import fighter from '../../images/traffic/fighter.png';
import helicopter from '../../images/traffic/helicopter.png';
import one_engine_prop from '../../images/traffic/one_engine_prop.png';
import four_engine_heavy_jet from '../../images/traffic/four_engine_heavy_jet.png';
import two_engine_turboprop from '../../images/traffic/two_engine_turboprop.png';
import two_engine_prop from '../../images/traffic/two_engine_prop.png';
import four_engine_turboprop from '../../images/traffic/four_engine_turboprop.png';
import concorde from '../../images/traffic/concorde.png';
import glider from '../../images/traffic/glider.png';
import { useAuth } from "../../context/AuthContext";

const iconMapping = {
  "two_engine_heavy_jet": two_engine_heavy_jet,
  "medium_jet": medium_jet,
  "fighter": fighter,
  "helicopter": helicopter,
  "one_engine_prop": one_engine_prop,
  "four_engine_heavy_jet": four_engine_heavy_jet,
  "two_engine_turboprop": two_engine_turboprop,
  "two_engine_prop": two_engine_prop,
  "four_engine_turboprop": four_engine_turboprop,
  "light_jet": light_jet,
  "concorde": concorde,
  "glider": glider,
  "four_engine_medium_jet": four_engine_medium_jet
};

const Traffic = ({ map, traffic, openWindows, setOpenWindows, setWindowSize, setRouteExists }) => {
  const [showGroundToggle, setShowGroundToggle] = useState(false);
  const [isVisible, setIsVisible] = useState(
    localStorage.getItem("trafficVisible") === "true"
  );
  const [showGroundTraffic, setShowGroundTraffic] = useState(true);
  const isInitialized = useRef(false);
  const iconLoaded = useRef(false);

  const [scrambleStatus, setScrambleStatus] = useState(null);
  const [selectedQraCallsign, setSelectedQraCallsign] = useState(null);

  const { user } = useAuth();

  // Re-render open windows when QRA state changes so inline content updates
  useEffect(() => {
    if (!openWindows || openWindows.length === 0) return;

    setOpenWindows(prevWindows =>
        prevWindows.map(win => {
          // Find if this window matches any current traffic item
          const matchingPilot = traffic.find(p => p.callsign === win.title);
          if (matchingPilot) {
            return {
              ...win,
              content: createWindowContent({
                callsign: matchingPilot.callsign,
                frul: matchingPilot.flight_plan?.flight_rules,
                atyp: matchingPilot.flight_plan?.aircraft,
                dep: matchingPilot.flight_plan?.departure,
                arr: matchingPilot.flight_plan?.arrival,
                altn: matchingPilot.flight_plan?.alternate,
                rfl: matchingPilot.flight_plan?.altitude,
                tas: matchingPilot.flight_plan?.cruise_tas,
                eobt: matchingPilot.flight_plan?.deptime,
                eet: matchingPilot.flight_plan?.enroute_time,
                route: matchingPilot.flight_plan?.route,
                rmk: matchingPilot.flight_plan?.remarks,
                pic: matchingPilot.name,
                gs: matchingPilot.groundspeed,
                hdg: matchingPilot.heading,
                alt: matchingPilot.altitude,
                iconType: matchingPilot.iconType || medium_jet,
              })
            };
          }
          return win;
        })
    );
  }, [selectedQraCallsign, scrambleStatus]);

  const getAirportName = (icaoCode) => {
    const airport = airportData.find((entry) => entry.icao === icaoCode);
    return airport ? airport.name : icaoCode; // If no match, return ICAO code itself
  };

  const addRouteToMap = (map, routeData) => {
    if (!map) return;
  
    // Convert route data into GeoJSON format
    const geojson = {
      type: "FeatureCollection",
      features: []
    };
  
    // Add waypoints as separate features
    routeData.forEach((point, index) => {
      geojson.features.push({
        type: "Feature",
        properties: { name: point.name },
        geometry: {
          type: "Point",
          coordinates: [parseFloat(point.coords[1]), parseFloat(point.coords[0])]
        }
      });
    });
  
    // Add the route line
    geojson.features.push({
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: routeData.map(point => [parseFloat(point.coords[1]), parseFloat(point.coords[0])])
      }
    });
  
    // Remove existing layers if they exist
    if (map.getSource("route")) {
      map.removeLayer("route-line");
      map.removeLayer("route-points");
      map.removeSource("route");
    }
  
    if (!map.getSource("route")) {
      map.addSource("route", { type: "geojson", data: geojson });
    }

    // Add the line layer
    map.addLayer({
      id: "route-line",
      type: "line",
      source: "route",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#ff5500", "line-width": 2 }
    });
  
    // Add the waypoint markers using the "fix-all" icon
    map.addLayer({
      id: "route-points",
      type: "symbol",
      source: "route",
      filter: ["==", "$type", "Point"],
      layout: {
        "icon-image": "fix-all",
        "icon-size": 0.8,
        "text-field": ["get", "name"],
        "text-size": 9,
        "text-offset": [0, 1],
        "text-anchor": "left",
        "text-font": ["Open Sans Bold"],
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "text-allow-overlap": true,
      },
      paint: {
        "text-color": "#000000",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1,
        "text-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          3.99, 0,   // Text invisible at zoom < 4
          4, 1       // Text visible at zoom >= 4
        ]
      }
    });    

    map.moveLayer("route-line", "traffic-layer");
    map.moveLayer("route-points", "traffic-layer");

    setRouteExists(true);
  };
  
  const fetchAndDisplayRoute = async (map, dep, route, arr) => {
    try {
      const response = await fetch(`${URL_START}/api/get-route?route=${dep}%20${route}%20${arr}`);
      const data = await response.json();
  
      if (data && Array.isArray(data)) {
        addRouteToMap(map, data);
      }
    } catch (error) {
      console.error("Error fetching route:", error);
    }
  };

  // Create GeoJSON data from traffic
  const createTrafficGeoJSON = useCallback((trafficData) => {
    return {
      type: "FeatureCollection",
      features: trafficData
        .filter((pilot) => showGroundTraffic || pilot.groundspeed >= 50)
        .map((pilot) => ({
          type: "Feature",
          properties: {
            callsign: pilot.callsign,
            heading: pilot.heading || 0,
            frul: pilot.flight_plan.flight_rules,
            atyp: pilot.flight_plan.aircraft,
            dep: pilot.flight_plan.departure,
            arr: pilot.flight_plan.arrival,
            altn: pilot.flight_plan.alternate,
            rfl: pilot.flight_plan.altitude,
            tas: pilot.flight_plan.cruise_tas,
            eobt: pilot.flight_plan.deptime,
            eet: pilot.flight_plan.enroute_time,
            route: pilot.flight_plan.route,
            rmk: pilot.flight_plan.remarks,
            pic: pilot.name,
            gs: pilot.groundspeed,
            hdg: pilot.heading,
            alt: pilot.altitude,
            iconType: pilot.iconType || medium_jet,
          },
          geometry: {
            type: "Point",
            coordinates: [pilot.longitude, pilot.latitude],
          },
        })),
    };
  }, [showGroundTraffic]);

  // Create traffic window content
  const createWindowContent = useCallback((properties) => {
    const {
      callsign, frul = "//", atyp = "//", dep = "//", arr = "//", altn = "",
      rfl = "//", tas = "//", eobt = "//", eet = "//", route = "//", rmk = "//",
      pic = "//", gs = "//", hdg = "//", alt = "//"
    } = properties;

    const flightLevel = rfl !== "//" ? Math.floor(Number(rfl) / 100) : "//";
    const formattedAlt = alt !== "//" ? alt.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : "//";
    const formattedHdg = hdg !== "//" ? String(hdg).padStart(3, '0') : "//";
    const prefix = callsign.substring(0, 3).toUpperCase();
    const hasNumbers = /\d/.test(callsign);
    const formattedCallsign = hasNumbers && airlines[prefix]
        ? `${airlines[prefix].toUpperCase()} ${callsign.substring(3)}`
        : callsign;
    const aircraftEntry = atypData.find((entry) => entry.icao === (atyp.includes("/") ? atyp.split("/")[0] : atyp));
    const longAtyp = aircraftEntry ? `${aircraftEntry.manufacturer} ${aircraftEntry.model}` : atyp.includes("/") ? atyp.split("/")[0] : atyp;

    const skyVectorUrl = `https://skyvector.com/?fpl=${dep}%20${route}%20${arr}`;

    // Handler to execute the scramble API call inline
    const handleScrambleRequest = async () => {
      setScrambleStatus('sending');
      try {
        const response = await fetch('https://backend.virtualfiaf.org/qra/scramble', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            atc_cid: user?.cid || "Unknown",
            target_callsign: callsign,
            target_aircraft_type: atyp
          }),
        });

        if (response.ok) {
          setScrambleStatus('success');
        } else {
          setScrambleStatus('error');
        }
      } catch (error) {
        console.error("Error sending QRA request:", error);
        setScrambleStatus('error');
      }
    };

    return (
        <div
            className="p-2 monospace-font text-sm overflow-y-auto relative"
            ref={(node) => {
              if (node && selectedQraCallsign === callsign) {
                node.scrollTop = node.scrollHeight;
              }
            }}
        >
          <div className="flex">
            <div className="pilotCallsign flex-1">{formattedCallsign}</div>
            <div className="pilotCallsign flex-1">{longAtyp}</div>
          </div>

          <div className="flex items-center" style={{alignItems: "stretch"}}>
            <div className="pilotdepdest flex-1">{dep} <span className="text-xs">{getAirportName(dep)}</span></div>
            <img className="airplaneIcon" src={airplaneIcon} alt="Airplane" />
            <div className="pilotdepdest flex-1">{arr} <span className="text-xs">{getAirportName(arr)}</span></div>
          </div>

          <div className="pilotText">{gs} kt</div>
          <div className="pilotText">{formattedAlt} ft</div>
          <div className="pilotText">{formattedHdg}°</div>
          <p className="pl-[2px]">ICAO FLIGHT PLAN</p>
          <div className="flightplan text-xs">
            (FPL-<b className="highlight">{callsign}</b>-{frul}S <br />
            -{atyp} <br />
            -<b className="highlight">{dep}</b>{eobt} <br />
            -N0{tas}F{flightLevel} {route} <br />
            -<b className="highlight">{arr}</b>{eet} {altn}<br />
            -{rmk})
          </div>

          <div className="pilotCallsign pilotDetails flex-1 text-xs rounded-full">
            <a
                href={skyVectorUrl}
                onClick={(e) => { e.preventDefault(); window.open(skyVectorUrl, '_blank', 'width=800,height=600');}}
                style={{ cursor: 'pointer', textDecoration: 'underline'}}
            >
              <img className="w-[16px] h-auto mr-2 inline" src={openIcon} alt="Open" />
              Open in SkyVector
            </a>
          </div>

          <p className="pilotCallsign pilotDetails text-xs">
            <img className="w-[16px] h-auto mr-2 inline" src={userIcon} alt="PIC" />
            {pic}
          </p>

          {/* Styled Red QRA Button */}
          <button
              onClick={() => {
                setSelectedQraCallsign(selectedQraCallsign === callsign ? null : callsign);
                setScrambleStatus(null);
              }}
              className="mt-2 w-full py-1.5 px-3 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-full transition-colors flex items-center justify-center gap-1"
          >
            Quick Reaction Alert (vFiAF)
          </button>

          {/* Inline Expanded QRA Confirmation / Status Section */}
          {selectedQraCallsign === callsign && (
              <div className="mt-2 border bg-red-600 p-2.5 text-white text-xs">
                {scrambleStatus === null && (
                    <>
                      <p className="font-medium mb-1 highlight !text-white">Confirm QRA Request</p>
                      <p className="mb-2 text-white leading-tight">
                        Details will be sent to the Virtual Finnish Air Force. It is not guaranteed a pilot will be able to respond.
                      </p>
                      <div className="flex gap-2 justify-end">
                        <button
                            onClick={() => setSelectedQraCallsign(null)}
                            className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 text-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                            onClick={handleScrambleRequest}
                            className="px-2.5 py-1 bg-white hover:text-black font-bold text-red-600"
                        >
                          Confirm Scramble
                        </button>
                      </div>
                    </>
                )}

                {scrambleStatus === 'sending' && (
                    <p className="text-center py-2 text-yellow-400 font-semibold">Sending QRA request...</p>
                )}

                {scrambleStatus === 'success' && (
                    <>
                      <p className="font-bold highlight !text-green-400 mb-1">Alert Sent Successfully</p>
                      <p className="mb-2 text-white leading-relaxed">
                        The Quick Reaction Alert has been sent. If a pilot is available, they will evaluate the situation when the alert has been noticed. Pilots will not respond if the aircraft has been disconnected, a supervisor has been involved or if the issue has been resolved.
                      </p>
                      <div className="flex justify-end">
                        <button
                            onClick={() => setSelectedQraCallsign(null)}
                            className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 text-gray-200"
                        >
                          Close
                        </button>
                      </div>
                    </>
                )}

                {scrambleStatus === 'error' && (
                    <>
                      <p className="font-bold highlight !text-white mb-1">Transmission Failed</p>
                      <p className="mb-2 text-white leading-relaxed">
                        Failed to send the QRA request. Please check your connection and try again.
                      </p>
                      <div className="flex justify-end">
                        <button
                            onClick={() => setSelectedQraCallsign(null)}
                            className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 text-gray-200"
                        >
                          Close
                        </button>
                      </div>
                    </>
                )}
              </div>
          )}
        </div>
    );
  }, [selectedQraCallsign, scrambleStatus, user]);

  // Handle traffic layer click
  const handleTrafficClick = useCallback((e) => {
    const callsign = e.features[0].properties.callsign;
    const properties = e.features[0].properties;

    console.log("Traffic clicked");

    setOpenWindows(prev => {
      const existingIndex = prev.findIndex(win => win.title === callsign);

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          content: createWindowContent(properties)
        };
        return updated;
      }

      return [
        ...prev,
        {
          id: `traffic-window-${callsign}-${Date.now()}`,
          title: callsign,
          content: createWindowContent(properties)
        }
      ];
    });
    setWindowSize(2);
  }, [setOpenWindows, createWindowContent]);

  // Initialize traffic layer
  const initializeTrafficLayer = useCallback(() => {
    if (!map || !traffic || !Array.isArray(traffic)) return;

    const geojsonData = createTrafficGeoJSON(traffic);

    if (!map.getSource("traffic")) {
      map.addSource("traffic", { type: "geojson", data: geojsonData });

      map.addLayer({
        id: "traffic-layer",
        type: "symbol",
        source: "traffic",
        layout: {
          "icon-image": ["get", "iconType"],
          "icon-size": 0.25,
          "icon-rotate": ["get", "heading"],
          "icon-allow-overlap": true,
          "text-field": ["get", "callsign"],
          "text-size": 10,
          "text-offset": [1, 0],
          "text-anchor": "left",
          "text-allow-overlap": true,
          "text-font": ["Open Sans Bold"],
          "visibility": isVisible ? "visible" : "none",
        },
        paint: {
          "text-color": "black",
          "text-halo-color": "white",
          "text-halo-width": 1,
          "text-opacity": 0
        },
      });

      map.moveLayer('traffic-layer');

      // Set up event handlers only after the layer is added to the map
      map.on("mouseenter", "traffic-layer", () => {
        // Check if the layer exists before updating layout
        if (map.getLayer('traffic-layer')) {
          map.setPaintProperty("traffic-layer", "text-opacity", 1); // Show text (callsign) on hover
          map.getCanvas().style.cursor = "pointer";
        }
      });

      map.on("mouseleave", "traffic-layer", () => {
        // Check if the layer exists before updating layout
        if (map.getLayer('traffic-layer')) {
          map.setPaintProperty("traffic-layer", "text-opacity", 0); // Hide text (callsign) when mouse leaves
          map.getCanvas().style.cursor = "default";
        }
      });
    } else {
      map.getSource("traffic").setData(geojsonData);
    }

    isInitialized.current = true;
  }, [map, traffic, isVisible, createTrafficGeoJSON]);

  // Load the traffic icon
  const loadTrafficIcon = useCallback(() => {
    if (!map.hasImage(medium_jet)) {
      // Load the default icon first
      map.loadImage(medium_jet, (error, image) => {
        if (error) {
          console.error("Error loading traffic icon:", error);
          return;
        }
        // add default traffic icon
        if (!map.hasImage('medium_jet')) {
          map.addImage('medium_jet', image);
        }
        iconLoaded.current = true;
        initializeTrafficLayer();
      });
    } else {
      iconLoaded.current = true;
      initializeTrafficLayer();
    }

    // Dynamically load additional icons based on the iconType
    traffic.forEach((pilot) => {
      const iconType = pilot.iconType || medium_jet;  // Default to medium_jet
      const iconImage = iconMapping[iconType] || medium_jet; // Use the image imported

      if (!map.hasImage(iconType)) {
        map.loadImage(iconImage, (error, image) => {
          if (error) {
            console.error(`Error loading icon for ${iconType}:`, error);
            return;
          }

          try {
            if (!map.hasImage(iconType)) {
              map.addImage(iconType, image);
            }
          } catch (e) {
            console.log(`Image ${iconType} already exists`);
          }
        });
      }
    });
  }, [map, traffic, initializeTrafficLayer]);

  // Update traffic visibility
  const updateVisibility = useCallback(() => {
    if (!map || !map.getLayer("traffic-layer")) return;

    map.setLayoutProperty(
      "traffic-layer",
      "visibility",
      isVisible ? "visible" : "none"
    );

    if (isVisible && traffic) {
      const geojsonData = createTrafficGeoJSON(traffic);
      if (map.getSource("traffic")) {
        map.getSource("traffic").setData(geojsonData);
      }
    }
  }, [map, isVisible, traffic, createTrafficGeoJSON]);

  // Toggle visibility
  const toggleVisibility = useCallback(() => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    localStorage.setItem("trafficVisible", newVisibility.toString());
  }, [isVisible]);

  // Toggle ground traffic visibility
  const toggleGroundTraffic = useCallback(() => {
    setShowGroundTraffic(prev => !prev);
  }, []);

  // Main effect for setting up and updating traffic
  useEffect(() => {
    if (!map) return;

    const handleMapLoad = () => {
      loadTrafficIcon();
    };

    if (map) {
      handleMapLoad();
    } else {
      map.on("load", handleMapLoad);
    }

    // Add click handler when component mounts
    map.on('click', 'traffic-layer', handleTrafficClick);

    return () => {
      map.off("load", handleMapLoad);
      // Only remove the specific handler we added
      map.off('click', 'traffic-layer', handleTrafficClick);
    };
  }, [map, loadTrafficIcon, handleTrafficClick]);

  // Effect for updating traffic data and visibility
  useEffect(() => {
    if (!map || !iconLoaded.current) return;

    if (isInitialized.current) {
      updateVisibility();
      // Re-attach click handler whenever traffic updates
      map.on('click', 'traffic-layer', handleTrafficClick);
    } else {
      initializeTrafficLayer();
    }

    return () => {
      // Cleanup click handler when dependencies change
      map.off('click', 'traffic-layer', handleTrafficClick);
    };
  }, [map, traffic, isVisible, initializeTrafficLayer, updateVisibility, handleTrafficClick]);

  return (
    <div
      onMouseEnter={() => setShowGroundToggle(true)}
      onMouseLeave={() => setShowGroundToggle(false)}
      style={{width: "70px", backgroundColor: "transparent"}}
    >

      <button
        data-tooltip-id="map-buttons-tooltip"
        data-tooltip-content=""
        className={`w-8 h-8 ${isVisible ? "bg-gray-600" : "bg-gray-400"} text-white flex items-center justify-center`}
        onClick={toggleVisibility}
        
      >
        <img className="w-5" src={trafficIcon} alt="Traffic" />
      </button>
      {isVisible && showGroundToggle && (
        <button
          data-tooltip-id="map-buttons-tooltip"
          data-tooltip-content=""
          className={`w-8 h-8 ${showGroundTraffic ? "bg-gray-600" : "bg-gray-400"} text-white flex items-center justify-center`}
          onClick={toggleGroundTraffic}
          style={{position: "absolute", top: "70px", left: "40px"}}
        >
          <img className="w-5" src={onGroundIcon} alt="Ground Traffic" />
        </button>
      )}
    </div>
  );
};


export default Traffic;


